import opConfig from 'openpathConfig';
import {
  PossibleRegionEnvs,
  getRegionFromHeliumUrl,
} from './getRegionFromHeliumUrl';

export type Logins = Api.Response['listCloudServerRegions'][0] & {
  namespaces: Api.Response['determineLoginCandidateNamespaces'];
};

const HELIUM_ENDPOINT = opConfig.PORTAL_API_URL;

interface LoginOptions {
  env?: string | null;
  orgId?: number | null;
  isDmp?: boolean;
}

interface NamespaceOptions {
  orgId?: number | null;
  isDmp?: boolean;
}

type LoginCandidateNamespace = Utils.SingularOfType<
  Api.Response['determineLoginCandidateNamespaces']
>;

type LoginCandidateNamespaceWithRegion = LoginCandidateNamespace & {
  region: PossibleRegionEnvs;
};

/**
 * @description function to be used inside Array.filter of namespaces based on SSO, orgId, and isDmp
 * filter out SSO enabled namespaces
 * filter out namespaces with no users with a matching orgId
 *   - This filters on matchedIdentity.users (they can be imported from this namespace into the org matching the orgId)
 * filter out namespaces where user with matching orgId that is dmp or any users with a dmp org when isDmp is true
 *   - This filters on matchedIdentity.users (they can be imported from this namespace into the org that has dmp)
 * @returns boolean
 */
const namespaceFilter = (
  namespace: Logins['namespaces'][0],
  { orgId, isDmp }: NamespaceOptions,
): boolean => {
  const MASTERMODE_NAMESPACE_TYPE_ID = 1;

  /**
   * For all namespaces outside of global/mastermode: when there are no active
   * users the system should not allow using the namespace
   */
  if (
    namespace.namespaceType?.id !== MASTERMODE_NAMESPACE_TYPE_ID &&
    (!namespace.matchedIdentity?.users ||
      namespace.matchedIdentity?.users.length === 0)
  ) {
    return false;
  }

  // Remove any mobile SSO Enabled namespaces
  if (
    !namespace.identityProvider?.isSsoEnabled &&
    namespace.identityProvider?.isMobileSsoEnabled
  ) {
    return false;
  }

  const users = namespace.matchedIdentity?.users ?? [];

  // namespace is only valid if any user is in the org requested
  if (orgId) {
    // If logging into dmp - the user's matching org needs to be a dmp org
    return (
      users.findIndex(
        (user) =>
          user.org?.id === orgId &&
          (!isDmp || user.org.orgAwareConfig?.dmpOrgIdExt),
      ) > -1
    );
  }

  // If logging into dmp - at least one user's org needs to be a dmp org otherwise any will do
  return (
    !isDmp ||
    users.findIndex((user) => user.org?.orgAwareConfig?.dmpOrgIdExt) > -1
  );
};

/**
 * @description function that retrieves available regions for the current environment and returns
 * the non sso enabled namespaces matching the given email and filtered appropriately per the env, orgId, isDmp options.
 * @returns Promise<Logins[]>
 */
export const getPossibleLogins = async (
  email: string,
  { env, orgId, isDmp }: LoginOptions = {},
): Promise<Logins[]> => {
  const regionsResponse = await fetch(`${HELIUM_ENDPOINT}/cloudServerRegions`);

  const regions: {
    data: Api.Response['listCloudServerRegions'];
  } = await regionsResponse.json();

  const namespacesResponse = await Promise.all(
    regions.data
      // only call for the region matching the given env if exists
      .filter((region) => !env || region.env === env)
      .map((region) =>
        fetch(
          `${region.helium?.endpoint}/auth/determineLoginCandidateNamespaces`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email }),
          },
        ),
      ),
  );

  const namespaceDataResponse: Array<
    LoginCandidateNamespaceWithRegion[] | null
  > = await Promise.all(
    namespacesResponse.map(async (res) => {
      /**
       * Inject candidate regions into their data so we can be more exact on which region they belong to
       * The url we hit indicates which api this came from
       */
      const region = getRegionFromHeliumUrl(res.url);

      const json = await res.json();

      // Check if no namespaces found - email not found returns a 400
      if (res.status === 400 && json.errorData?.errorCode === 'H-2102') {
        return null;
      }

      return [...json.data].map<LoginCandidateNamespaceWithRegion>(
        (data: LoginCandidateNamespace) => ({
          ...data,
          region,
        }),
      );
    }),
  );

  // Filter out the regions where no namespaces were found
  // Need to use typeGaurd method in filter to get typesafety
  const namespaceData = namespaceDataResponse.filter(
    (
      maybeData: LoginCandidateNamespaceWithRegion[] | null,
    ): maybeData is LoginCandidateNamespaceWithRegion[] => !!maybeData,
  );

  const possibleLogins: Logins[] = [];
  namespaceData.forEach((namespaces) => {
    // filter out SSO enabled namespaces and filter out namespaces with no users with a matching orgId and handle isDmp
    const allowedNamespaces = namespaces?.filter((namespace) =>
      namespaceFilter(namespace, { orgId, isDmp }),
    );

    if (allowedNamespaces?.length > 0) {
      /**
       * Using filter here is a bit less idiomatic here than `Array.find` but
       * coalesces more smoothly with TypeScript compilation.
       */
      const [region] = regions.data.filter(
        // allowedNamespaces are a group of namespaces from the same region so we can use the first one's region
        ({ env: regionEnv }) => regionEnv === allowedNamespaces[0].region,
      );

      possibleLogins.push({
        ...region,
        namespaces: allowedNamespaces,
      });
    }
  });

  return possibleLogins;
};
